import {
  ChangeabilityRequest as ProtoChangeabilityRequest,
  ChangeabilityResponse as ProtoChangeabilityResponse,
  CanUpdateChangeabilityRequest as ProtoCanUpdateChangeabilityRequest,
  CanUpdateChangeabilityResponse as ProtoCanUpdateChangeabilityResponse,
  CanUpdateChangeabilityResponse_CanUpdate,
} from "@opstel/gen/knop/ui/v1/api/changeability_pb";
import { EpdResponse as ProtoEpdResponse } from "@opstel/gen/knop/ui/v1/api/epd_pb";
import {
  HipTasksResponse as ProtoHipTasksResponse,
  OpenHipPlanByTaskIdRequest as ProtoOpenHipPlanByTaskIdRequest,
  OpenHipPlanByTaskIdResponse as ProtoOpenHipPlanByTaskIdResponse,
  HideHipPlanByTaskIdRequest as ProtoHideHipPlanByTaskIdRequest,
  HideHipPlanByTaskIdResponse as ProtoHideHipPlanByTaskIdResponse,
  GenerateSolutionRequest,
  GenerateSolutionResponse as ProtoGenerateSolutionResponse,
} from "@opstel/gen/knop/ui/v1/api/hip_pb";
import {
  PlanTrainUnitDetailsResponse as ProtoPlanTrainUnitDetailsResponse,
  PlanServiceTaskRequest as ProtoPlanServiceTaskRequest,
  PlanServiceTaskResponse as ProtoPlanServiceTaskResponse,
  UnplanServiceTaskRequest as ProtoUnplanServiceTaskRequest,
  UnplanServiceTaskResponse as ProtoUnplanServiceTaskResponse,
  PinTransitionRequest,
  UnpinTransitionRequest,
  PinTransitionResponse as ProtoPinTransitionResponse,
  UnpinTransitionResponse as ProtoUnpinTransitionResponse,
} from "@opstel/gen/knop/ui/v1/api/plan_train_unit_details_pb";
import { RetShiftResponse as ProtoRetShiftResponse } from "@opstel/gen/knop/ui/v1/api/retshifts_pb";
import { TrackOccupationsResponse as ProtoTrackOccupationsResponse } from "@opstel/gen/knop/ui/v1/api/sbd_pb";
import { Action as ProtoAction } from "@opstel/gen/knop/ui/v1/model/action_pb";
import { ScenarioResponse as ProtoScenarioResponse } from "@opstel/gen/knop/ui/v1/api/scenario_pb";
import { z } from "zod/v3";
import { EPDData, parseEpdData } from "./api/epdData";
import { HipTasks, parseHipTasksData } from "./api/hipTasks";
import { RetShifts, parseRetShiftsData } from "./api/retShiftResponse";
import { SBDData, parseSbdData } from "./api/sbdData";
import { Scenario, scenarioSchema } from "./api/scenario";
import { KnopClient } from "./knopClient";
import { ProtoValidationError } from "./protoValidationError";
import { KapiInstanceMetadata } from "./models/kapiInstanceMetadata";
import { parseKapiInstanceList } from "./api/kapiInstanceList";
import {
  ImportKapiInstanceListRequest as ProtoImportKapiInstanceListRequest,
  ImportKapiInstanceListResponse as ProtoImportKapiInstanceListResponse,
  ImportKapiInstanceByNameRequest as ProtoImportKapiInstanceByNameRequest,
  ImportKapiInstanceByNameResponse as ProtoImportKapiInstanceByNameResponse,
  DownloadInstanceAsFileResponse,
  OpenInstanceFromFileRequest,
  DownloadInstanceAsFileRequest,
  OpenInstanceFromFileResponse as ProtoImportInstanceFromFileResponse,
  InstanceListResponse as ProtoInstanceListResponse,
} from "@opstel/gen/knop/ui/v1/api/instance_loading_pb";
import { LocationMetadata } from "./models/locationMetadata";
import { parseLocationList } from "./api/locationList";
import { LocationListResponse } from "@opstel/gen/knop/ui/v1/api/location_list_pb";
import { parseServiceTaskData } from "./api/planTrainUnitDetailsResponse";
import { PlanTrainUnitDetails } from "./models/planTrainUnitDetails/planTrainUnitDetails";
import { PlainMessage } from "@bufbuild/protobuf";
import {
  SendToDonnaRequest as ProtoSendToDonnaRequest,
  SendToDonnaResponse as ProtoSendToDonnaResponse,
} from "@opstel/gen/knop/ui/v1/api/send_to_donna_pb";
import { ResponseStatus } from "@opstel/gen/knop/ui/v1/api/util/response_status_pb";
import {
  ImportKapiInstanceByNameResponse,
  importKapiInstanceByNameResponseSchema,
} from "./api/importKapiInstanceByNameResponse";
import { openHipPlanByTaskIdResponseSchema } from "./api/openHipPlanByHipTaskId";
import {
  GenerateSolutionResponse,
  parseGenerateSolutionResponse,
} from "./api/generateSolutionResponse";
import {
  OpenInstanceFromFileResponse,
  openInstanceFromFileResponseSchema,
} from "./api/openInstanceFromFileResponse";
import { CorrelationId } from "@shared/telemetry/correlationId";
import { useCurrentInstanceId } from "@shared/common/contexts/instanceMetadata/useInstanceId";
import { InstanceMetadata } from "./models/instanceMetadata";
import { parseInstanceList } from "./api/instanceList";
import { Action } from "./v2/models/action";
import { parseAction } from "./models/action";

type FunctionProperties<T> = {
  // oxlint-disable-next-line typescript/no-explicit-any
  [K in keyof T]: T[K] extends (...args: any[]) => any ? K : never;
}[keyof T];

type KnopClientFunctionNames = FunctionProperties<KnopClient>;

// IMPROVE: URL endpoint structure, see OpstelServer for details
const apiMappings: Record<KnopClientFunctionNames, string> = {
  getKapiInstanceList: "/bff/instance-list",
  getInstanceList: "/bff/instances",
  getLocationList: "/bff/location-list",
  importKapiInstanceByName: "/bff/open-instance",
  getScenario: "/bff/scenario",
  getSbdData: "/bff/track-occupations",
  getAction: "/bff/action",
  getHipTasks: "/bff/hip-tasks",
  getEpdData: "/bff/epd-data",
  getRetShifts: "/bff/ret-shifts",
  postChangeability: "/bff/changeability",
  canUpdateChangeability: "/bff/can-update-changeability",
  openHipPlanByTaskId: "/bff/open-hip-plan-by-taskid",
  hideHipPlanByTaskId: "/bff/hide-hip-plan-by-taskid",
  openPreHipPlanByTaskId: "/bff/open-pre-hip-plan-by-taskid",
  generateSolution: "/bff/generate-solution",
  getPlanTrainUnitDetails: "/bff/plan-train-unit-details",
  planServiceTask: "/bff/plan-service-task",
  unplanServiceTask: "/bff/unplan-service-task",
  pinTransition: "/bff/pin-transition",
  unpinTransition: "/bff/unpin-transition",
  sendToDonna: "/bff/send-to-donna",
  openInstance: "/bff/open-serialized-instance-from-string",
  downloadInstance: "/bff/instance-as-serialized-string",
};

export type UrlParams = Record<string, string | undefined>;

export type GetRequest = { url: string; urlParams?: UrlParams; correlationId: CorrelationId };
export type PostRequest = GetRequest & { data: string };

function getCurrentCorrelationId(): CorrelationId {
  const correlationId = useCurrentInstanceId.getState().correlationId;
  if (!correlationId) {
    console.warn("Expected a correlation id but none was found, creating a new one");
    return CorrelationId.create();
  }
  return correlationId;
}
function setCurrentCorrelationId(correlationId: CorrelationId) {
  useCurrentInstanceId.getState().setCorrelationId(correlationId);
}

export abstract class BaseKnopClient implements KnopClient {
  knopClientType: string = "BaseKnopClient";

  abstract get(request: GetRequest): Promise<string>;

  abstract post(request: PostRequest): Promise<string>;

  async getScenario(instanceId: string | undefined): Promise<Scenario | null> {
    if (instanceId === undefined) return null;
    const responseText = await this.get({
      url: apiMappings.getScenario,
      urlParams: { instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoScenarioResponse.fromJsonString(responseText);
    try {
      return scenarioSchema.parse(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof z.ZodError) {
        return null;
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getSbdData(instanceId: string | undefined): Promise<SBDData | null> {
    if (instanceId === undefined) return null;
    const jsonString = await this.get({
      url: apiMappings.getSbdData,
      urlParams: { instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoTrackOccupationsResponse.fromJsonString(jsonString);
    if (proto.trackOccupations.length === 0) {
      return null;
    }
    try {
      return parseSbdData(proto);
    } catch (e) {
      // istanbul ignore else -- @preserve
      if (e instanceof z.ZodError) {
        console.error("Could not parse KnoP trackOccupationsResponse");
        console.error(e.message);
        return null;
      }
      // istanbul ignore next -- @preserve
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getAction(instanceId: string, actionId: string): Promise<Action | null> {
    const jsonString = await this.get({
      url: apiMappings.getAction,
      urlParams: { instanceId: instanceId, actionId: actionId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoAction.fromJsonString(jsonString);
    try {
      return parseAction(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof z.ZodError) {
        return null;
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getHipTasks(): Promise<HipTasks | null> {
    const response = await this.get({
      url: apiMappings.getHipTasks,
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoHipTasksResponse.fromJsonString(response);
    try {
      return parseHipTasksData(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof ProtoValidationError) {
        return null;
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getEpdData(instanceId: string | undefined): Promise<EPDData | null> {
    if (instanceId === undefined) return null;
    const response = await this.get({
      url: apiMappings.getEpdData,
      urlParams: { instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoEpdResponse.fromJsonString(response);
    if (!proto.actions) {
      return null;
    }
    if (!proto.location) {
      return null;
    }
    try {
      return parseEpdData(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof z.ZodError) {
        console.error("Could not parse KnoP EpdResponse");
        console.error(e.message);
        return null;
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getRetShifts(instanceId: string | undefined): Promise<RetShifts | null> {
    if (instanceId === undefined) return null;
    const response = await this.get({
      url: apiMappings.getRetShifts,
      urlParams: { instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoRetShiftResponse.fromJsonString(response);
    try {
      return parseRetShiftsData(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof ProtoValidationError) {
        return { status: "FAILED", retShifts: [] };
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async postChangeability(request: PlainMessage<ProtoChangeabilityRequest>): Promise<void> {
    const data = JSON.stringify(new ProtoChangeabilityRequest(request).toJson());
    const response = await this.post({
      url: apiMappings.postChangeability,
      data,
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoChangeabilityResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
  }

  async canUpdateChangeability(
    request: PlainMessage<ProtoCanUpdateChangeabilityRequest>,
  ): Promise<{ description: string; canUpdate: boolean }> {
    const data = JSON.stringify(new ProtoCanUpdateChangeabilityRequest(request).toJson());
    const response = await this.post({
      url: apiMappings.canUpdateChangeability,
      data,
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoCanUpdateChangeabilityResponse.fromJsonString(response);
    if (proto.result === CanUpdateChangeabilityResponse_CanUpdate.ERROR) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
    return {
      description: proto.description,
      canUpdate: proto.result === CanUpdateChangeabilityResponse_CanUpdate.YES,
    };
  }

  async openHipPlanByTaskId(
    request: PlainMessage<ProtoOpenHipPlanByTaskIdRequest>,
  ): Promise<{ instanceId: string }> {
    const response = await this.post({
      url: apiMappings.openHipPlanByTaskId,
      data: new ProtoOpenHipPlanByTaskIdRequest(request).toJsonString(),
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoOpenHipPlanByTaskIdResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
    try {
      const response = openHipPlanByTaskIdResponseSchema.parse(proto);
      setCurrentCorrelationId(CorrelationId.fromProto(response));
      return response;
    } catch (e) {
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async openPreHipPlanByTaskId(
    request: PlainMessage<ProtoOpenHipPlanByTaskIdRequest>,
  ): Promise<{ instanceId: string }> {
    const response = await this.post({
      url: apiMappings.openPreHipPlanByTaskId,
      data: new ProtoOpenHipPlanByTaskIdRequest(request).toJsonString(),
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoOpenHipPlanByTaskIdResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
    try {
      const response = openHipPlanByTaskIdResponseSchema.parse(proto);
      setCurrentCorrelationId(CorrelationId.fromProto(response));
      return response;
    } catch (e) {
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async hideHipPlanByTaskId(request: PlainMessage<ProtoHideHipPlanByTaskIdRequest>): Promise<void> {
    const response = await this.post({
      url: apiMappings.hideHipPlanByTaskId,
      data: new ProtoHideHipPlanByTaskIdRequest(request).toJsonString(),
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoHideHipPlanByTaskIdResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.message });
      throw error;
    }
  }

  async getKapiInstanceList(
    request: ProtoImportKapiInstanceListRequest,
  ): Promise<KapiInstanceMetadata[]> {
    const response = await this.post({
      url: apiMappings.getKapiInstanceList,
      data: request.toJsonString(),
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoImportKapiInstanceListResponse.fromJsonString(response);
    try {
      return parseKapiInstanceList(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof ProtoValidationError) {
        console.error("Could not parse instance list");
        console.error(e);
        return [];
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getInstanceList(): Promise<InstanceMetadata[]> {
    const response = await this.get({
      url: apiMappings.getInstanceList,
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoInstanceListResponse.fromJsonString(response);
    try {
      return parseInstanceList(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof ProtoValidationError) {
        console.error("Could not parse instance list");
        console.error(e);
        return [];
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async importKapiInstanceByName(
    request: ProtoImportKapiInstanceByNameRequest,
  ): Promise<ImportKapiInstanceByNameResponse> {
    setCurrentCorrelationId(CorrelationId.create());
    const response = await this.post({
      url: apiMappings.importKapiInstanceByName,
      data: request.toJsonString(),
      correlationId: getCurrentCorrelationId(),
    });
    const proto = ProtoImportKapiInstanceByNameResponse.fromJsonString(response);

    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
    try {
      return importKapiInstanceByNameResponseSchema.parse(proto);
    } catch (e) {
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getLocationList(): Promise<LocationMetadata[]> {
    const response = await this.get({
      url: apiMappings.getLocationList,
      correlationId: CorrelationId.create(),
    });
    const proto = LocationListResponse.fromJsonString(response);
    try {
      return parseLocationList(proto);
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof ProtoValidationError) {
        console.error("Could not parse location list");
        console.error(e);
        return [];
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async generateSolution(request: GenerateSolutionRequest): Promise<GenerateSolutionResponse> {
    const response = await this.post({
      url: apiMappings.generateSolution,
      data: request.toJsonString(),
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoGenerateSolutionResponse.fromJsonString(response);
    if (proto.status == ResponseStatus.FAILED && proto.failureDescription) {
      const error = new Error("Unexpected error", { cause: proto.failureDescription });
      throw error;
    }
    try {
      return parseGenerateSolutionResponse(proto);
    } catch (e) {
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async getPlanTrainUnitDetails(
    instanceId: string | undefined,
  ): Promise<PlanTrainUnitDetails[] | null> {
    if (instanceId === undefined) return null;
    const response = await this.get({
      url: apiMappings.getPlanTrainUnitDetails,
      urlParams: { instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoPlanTrainUnitDetailsResponse.fromJsonString(response);
    try {
      return parseServiceTaskData(proto).planTrainUnitDetailsList;
    } catch (e) {
      /* istanbul ignore else -- @preserve */
      if (e instanceof ProtoValidationError) {
        console.error("Could not parse service task list");
        console.error(e);
        return [];
      }
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }

  async planServiceTask(request: ProtoPlanServiceTaskRequest): Promise<void> {
    const response = await this.post({
      url: apiMappings.planServiceTask,
      data: request.toJsonString(),
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoPlanServiceTaskResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
  }

  async unplanServiceTask(request: ProtoUnplanServiceTaskRequest): Promise<void> {
    const response = await this.post({
      url: apiMappings.unplanServiceTask,
      data: request.toJsonString(),
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoUnplanServiceTaskResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
  }

  async pinTransition(request: PlainMessage<PinTransitionRequest>): Promise<void> {
    const response = await this.post({
      url: apiMappings.pinTransition,
      data: new PinTransitionRequest(request).toJsonString(),
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoPinTransitionResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
  }

  async unpinTransition(request: PlainMessage<UnpinTransitionRequest>): Promise<void> {
    const response = await this.post({
      url: apiMappings.unpinTransition,
      data: new UnpinTransitionRequest(request).toJsonString(),
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoUnpinTransitionResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
  }

  async sendToDonna(instanceId: string): Promise<void> {
    const response = await this.post({
      url: apiMappings.sendToDonna,
      data: new ProtoSendToDonnaRequest({ instanceId }).toJsonString(),
      urlParams: { instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = ProtoSendToDonnaResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
  }

  async downloadInstance(
    request: PlainMessage<DownloadInstanceAsFileRequest>,
  ): Promise<DownloadInstanceAsFileResponse> {
    const response = await this.get({
      url: apiMappings.downloadInstance,
      urlParams: { instanceId: request.instanceId },
      correlationId: CorrelationId.createChild(getCurrentCorrelationId()),
    });
    const proto = DownloadInstanceAsFileResponse.fromJsonString(response);
    proto.fileName = proto.fileName || "knoP_instance.kpp";
    if (!proto?.base64Data) {
      const error = new Error("No file data received from server");
      throw error;
    }
    return proto;
  }

  async openInstance(
    request: PlainMessage<OpenInstanceFromFileRequest>,
  ): Promise<OpenInstanceFromFileResponse> {
    const response = await this.post({
      url: apiMappings.openInstance,
      data: new OpenInstanceFromFileRequest(request).toJsonString(),
      correlationId: CorrelationId.create(),
    });
    const proto = ProtoImportInstanceFromFileResponse.fromJsonString(response);
    if (proto.status !== ResponseStatus.SUCCESS) {
      const error = new Error("Unexpected error", { cause: proto.description });
      throw error;
    }
    try {
      const response = openInstanceFromFileResponseSchema.parse(proto);
      setCurrentCorrelationId(CorrelationId.fromProto(response));
      return response;
    } catch (e) {
      /* istanbul ignore next -- @preserve */
      throw new Error("Unexpected error", { cause: e });
    }
  }
}
